import React, { useEffect, useState } from "react";
import { adminGetEmailSettings, adminUpdateEmailSettings, adminSendTestEmail } from "../lib/api";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "../hooks/use-toast";
import { Loader2, Send, Mail, Check, AlertTriangle } from "lucide-react";

const EmailSettingsForm = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testTo, setTestTo] = useState("");

  useEffect(() => {
    adminGetEmailSettings()
      .then((d) => {
        setData(d);
        setTestTo(d.notify_email || "");
      })
      .catch(() => setData(null));
  }, []);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.smtp_password_set;
      delete payload.updated_at;
      const saved = await adminUpdateEmailSettings(payload);
      setData(saved);
      toast({ title: "Email settings saved" });
    } catch (err) {
      toast({ title: "Save failed", description: err?.response?.data?.detail || "Error", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const runTest = async () => {
    if (!testTo) {
      toast({ title: "Enter a test email address", variant: "destructive" });
      return;
    }
    setTesting(true);
    try {
      await adminSendTestEmail(testTo);
      toast({ title: "Test email sent", description: `Check ${testTo} in a moment. Also check spam.` });
    } catch (err) {
      toast({
        title: "Test failed",
        description: err?.response?.data?.detail || "Could not send. Check credentials.",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  if (!data) {
    return <div className="flex justify-center py-8"><Loader2 className="animate-spin text-[var(--ri-red)]" /></div>;
  }

  return (
    <div className="space-y-4">
      <div className="p-4 rounded-xl bg-[var(--ri-cream)] border border-[var(--ri-border)]">
        <div className="flex items-start gap-3">
          <Mail size={18} className="text-[var(--ri-red)] mt-0.5" />
          <div className="text-[13px] leading-[1.65] text-[var(--ri-ink-soft)]">
            <p className="font-medium text-[var(--ri-ink)]">Gmail SMTP setup</p>
            <ol className="mt-2 space-y-1 list-decimal pl-4">
              <li>Turn on 2-Step Verification on your Google account.</li>
              <li>Go to <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer" className="text-[var(--ri-red)] underline">myaccount.google.com/apppasswords</a> and generate an App Password (name it "Real Istanbul Tours").</li>
              <li>Paste the 16-character App Password below, tick "Enable email delivery", save, then click "Send test email".</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[13px]">SMTP host</Label>
          <Input value={data.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} className="mt-1" placeholder="smtp.gmail.com" />
        </div>
        <div>
          <Label className="text-[13px]">SMTP port</Label>
          <Input type="number" value={data.smtp_port || 587} onChange={(e) => set("smtp_port", Number(e.target.value))} className="mt-1" placeholder="587" />
          <p className="text-[11px] text-[var(--ri-muted)] mt-1">Gmail: 587 (TLS) or 465 (SSL)</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[13px]">SMTP username (your Gmail)</Label>
          <Input value={data.smtp_username || ""} onChange={(e) => set("smtp_username", e.target.value)} className="mt-1" placeholder="you@gmail.com" />
        </div>
        <div>
          <Label className="text-[13px]">
            SMTP App Password
            {data.smtp_password_set && <span className="ml-2 text-[11px] text-green-700 inline-flex items-center gap-1"><Check size={11} /> saved</span>}
          </Label>
          <Input
            type="password"
            value={data.smtp_password || ""}
            onChange={(e) => set("smtp_password", e.target.value)}
            className="mt-1 font-mono"
            placeholder={data.smtp_password_set ? "•••••••••••• (leave blank to keep)" : "16-char Gmail App Password"}
            autoComplete="new-password"
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[13px]">From email (sender address)</Label>
          <Input value={data.from_email || ""} onChange={(e) => set("from_email", e.target.value)} className="mt-1" placeholder="Usually same as SMTP username" />
        </div>
        <div>
          <Label className="text-[13px]">From name</Label>
          <Input value={data.from_name || ""} onChange={(e) => set("from_name", e.target.value)} className="mt-1" placeholder="Real Istanbul Tours" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[13px]">Notification inbox (where forms are delivered)</Label>
          <Input value={data.notify_email || ""} onChange={(e) => set("notify_email", e.target.value)} className="mt-1" placeholder="freeistanbultoursturkey@gmail.com" />
        </div>
        <div>
          <Label className="text-[13px]">Reply-to (optional)</Label>
          <Input value={data.reply_to || ""} onChange={(e) => set("reply_to", e.target.value)} className="mt-1" placeholder="Defaults to the guest's own email" />
        </div>
      </div>

      <div className="space-y-2 pt-2">
        <label className="flex items-start gap-2 text-[14px] cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium">Enable email delivery</span>
            <span className="block text-[12px] text-[var(--ri-muted)]">Turn this off to keep forms working without sending any emails.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-[14px] cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.send_admin_notification}
            onChange={(e) => set("send_admin_notification", e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium">Send admin notifications</span>
            <span className="block text-[12px] text-[var(--ri-muted)]">Every booking and inquiry goes to the inbox above.</span>
          </span>
        </label>
        <label className="flex items-start gap-2 text-[14px] cursor-pointer">
          <input
            type="checkbox"
            checked={!!data.send_guest_confirmation}
            onChange={(e) => set("send_guest_confirmation", e.target.checked)}
            className="mt-1"
          />
          <span>
            <span className="font-medium">Send guest auto-reply</span>
            <span className="block text-[12px] text-[var(--ri-muted)]">Guests get an instant confirmation email with the details of what they submitted.</span>
          </span>
        </label>
      </div>

      <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[var(--ri-border)]">
        <button onClick={save} disabled={saving} className="btn-primary inline-flex items-center gap-2">
          {saving && <Loader2 size={14} className="animate-spin" />}
          Save email settings
        </button>
        <div className="flex items-end gap-2 flex-1 min-w-[280px]">
          <div className="flex-1">
            <Label className="text-[13px]">Send test email to</Label>
            <Input
              value={testTo}
              onChange={(e) => setTestTo(e.target.value)}
              placeholder="you@example.com"
              className="mt-1"
              type="email"
            />
          </div>
          <button onClick={runTest} disabled={testing} className="btn-outline inline-flex items-center gap-2 whitespace-nowrap">
            {testing ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Send test
          </button>
        </div>
      </div>

      {!data.smtp_password_set && data.enabled && (
        <div className="p-3 rounded-lg bg-yellow-50 border border-yellow-200 text-[13px] text-yellow-900 flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>You've enabled email delivery but haven't set an SMTP password yet. Emails will silently fail until you add one.</span>
        </div>
      )}
    </div>
  );
};

export default EmailSettingsForm;
